import { useMemo, useState } from "react";
import { FlaskConical, Play, CheckCircle2, XCircle, AlertTriangle, Copy, Check, Terminal } from "lucide-react";
import api from "@/lib/api";

/**
 * Payload Tester — platform_admin sanity-checks VPS JSON payloads against the
 * exact Pydantic model each endpoint uses, without persisting anything.
 * Backed by POST /api/ingest/dry-run.
 *
 * Endpoint list matches the 9 keys registered in DRY_RUN_MODELS on the backend.
 * If a new ingest endpoint is added there, add it here too.
 */
const ENDPOINTS = [
  "/api/kpi/jobs",
  "/api/cancellations",
  "/api/l10/meetings",
  "/api/l10/rocks",
  "/api/l10/todos",
  "/api/l10/headlines",
  "/api/l10/ids",
  "/api/l10/scorecard",
  "/api/comp",
];

// Minimal starter payloads pulled straight from /app/docs/ingest_endpoints.md —
// help the operator see a valid shape without hand-typing every field.
const SAMPLES = {
  "/api/kpi/jobs": {
    job_number: "J-DRY-001",
    tech_name: "Curt McCarty",
    unit: "Springfield",
    customer: "Acme Manufacturing",
    scheduled_at: "2026-07-13T08:00:00+00:00",
    finished_at: "2026-07-13T11:45:00+00:00",
    revenue: 1850.0,
    status: "completed",
    week_of: "2026-07-13",
    items: [
      { key: "notes", answer: "YES" },
      { key: "photos_10plus", answer: "YES" },
      { key: "orange_highlight", answer: "NO" },
    ],
  },
  "/api/cancellations": {
    external_id: "sam-classify-2026-07-13-J100117",
    job_id: "J-100117",
    customer: "Smith Residence",
    cancelled_at: "2026-07-13T09:14:22+00:00",
    bucket: "HARD_CANCEL",
    reason: "Customer no-show, no callback within 48h",
    value: 425.0,
    unit: "Springfield",
  },
  "/api/l10/meetings": {
    week_of: "2026-07-13",
    rating: 9,
    cascade_notes: "Focus next week on OBP for the Central MO unit.",
    manual_headline_note: "Curt McCarty crossed $10k for the first time this year.",
    next_meeting_date: "2026-07-20",
  },
  "/api/l10/rocks": {
    external_id: "rock-q3-2026-01",
    quarter: "Q3 2026",
    title: "Launch Rilla for all techs by end of Q3",
    owner: "Brett Payne",
    due_date: "2026-09-30",
    on_track: true,
    dismissed: false,
  },
  "/api/l10/todos": {
    external_id: "todo-2026-07-13-01",
    action: "Schedule 1:1 with Curt to review OBP trajectory",
    owner: "Brett Payne",
    due_date: "2026-07-18",
    completed: false,
  },
  "/api/l10/headlines": {
    external_id: "headline-2026-W29-01",
    week_of: "2026-07-13",
    kind: "revenue_leader",
    text: "James Pederson: $12,340 — top of the wall three weeks running.",
    auto_generated: false,
  },
  "/api/l10/ids": {
    external_id: "ids-2026-W29-revenue",
    week_of: "2026-07-13",
    title: "Issue #1 — Weekly Revenue below target",
    body: "Team missed $65k target by $8.4k (12.9%). Two techs below OBP floor.",
    metric_key: "revenue",
    suggested_owner: "Brett Payne",
    auto_generated: true,
  },
  "/api/l10/scorecard": {
    week_of: "2026-07-13",
    external_id: "claire-scorecard-2026-W29",
    total_revenue: 62285.0,
    revenue_by_unit: {
      Springfield: 32105.0,
      Joplin: 18450.0,
      "Central MO": 11730.0,
    },
    conversion_rate: 0.42,
    close_rate: 0.61,
    avg_ticket: 1638.55,
    tech_performance: [
      { tech_name: "James Pederson", revenue: 12340.0, obp: 1830.0, jobs: 7, close_rate: 0.71, avg_ticket: 1762.86 },
      { tech_name: "Chris Prather",  revenue: 10940.0, obp: 1620.0, jobs: 8, close_rate: 0.62, avg_ticket: 1367.50 },
    ],
    source_run_id: "claire-run-2026-07-14T06:00:00Z",
  },
  "/api/comp": {
    agent_name: "raymond",
    run_status: "completed",
    records_processed: 42,
    last_period_checked: "2026-W29",
    summary: "Checked 42 records for 2026-W29 — no anomalies flagged",
    external_id: "raymond-2026-W29-run-abc123",
    // Financial fields below are intentionally included to demonstrate the
    // silent-drop behavior for /api/comp — the dry-run will list them in
    // dropped_fields to confirm the schema is rejecting them at the boundary.
    revenue: 99999,
    ktb_earned: 500,
  },
};

export default function PayloadTester() {
  const [endpoint, setEndpoint] = useState(ENDPOINTS[0]);
  const [text, setText] = useState(() => JSON.stringify(SAMPLES[ENDPOINTS[0]], null, 2));
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null); // { valid, errors, dropped_fields, parsed, model, endpoint }
  const [parseErr, setParseErr] = useState("");
  const [copied, setCopied] = useState(false);
  const [curlCopied, setCurlCopied] = useState(false);

  const loadSample = (ep) => {
    setEndpoint(ep);
    setText(JSON.stringify(SAMPLES[ep], null, 2));
    setResult(null);
    setParseErr("");
  };

  const submit = async () => {
    setParseErr("");
    setResult(null);
    let payload;
    try {
      payload = JSON.parse(text);
    } catch (e) {
      setParseErr(`JSON parse error: ${e.message}`);
      return;
    }
    setRunning(true);
    try {
      const { data } = await api.post("/ingest/dry-run", { endpoint, payload });
      setResult(data);
    } catch (e) {
      setResult({
        valid: false,
        endpoint,
        model: null,
        errors: [{ loc: ["_transport"], msg: e.response?.data?.detail || e.message, type: "transport" }],
        dropped_fields: [],
        parsed: null,
      });
    } finally {
      setRunning(false);
    }
  };

  const copyResult = () => {
    if (!result) return;
    navigator.clipboard.writeText(JSON.stringify(result, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Build a working curl command using the server-normalized endpoint + the parsed
  // body (i.e. exactly what would be persisted, financial fields on /api/comp
  // already stripped). Wrapped in single quotes; embedded single quotes are
  // shell-escaped as '\'' so the -d payload stays intact.
  const copyAsCurl = () => {
    if (!result?.valid || !result.parsed || !result.endpoint) return;
    const bodyStr = JSON.stringify(result.parsed, null, 2).replace(/'/g, "'\\''");
    const cmd =
      `curl -s -X POST "$API_URL${result.endpoint}" \\\n` +
      `  -H "Authorization: Bearer $TOKEN" \\\n` +
      `  -H "Content-Type: application/json" \\\n` +
      `  -d '${bodyStr}'`;
    navigator.clipboard.writeText(cmd);
    setCurlCopied(true);
    setTimeout(() => setCurlCopied(false), 2000);
  };

  const statusBadge = useMemo(() => {
    if (!result) return null;
    if (result.valid) {
      return (
        <span
          data-testid="payload-tester-status-valid"
          className="inline-flex items-center gap-1 text-[11px] font-mono-data uppercase tracking-widest bg-[#e6f9f2] text-[#0e8a68] border border-[#c1eddc] rounded-full px-2.5 py-1 font-bold"
        >
          <CheckCircle2 size={12} strokeWidth={2.2} /> Valid
        </span>
      );
    }
    return (
      <span
        data-testid="payload-tester-status-invalid"
        className="inline-flex items-center gap-1 text-[11px] font-mono-data uppercase tracking-widest bg-[#fdecec] text-[#c73535] border border-[#f4c9c9] rounded-full px-2.5 py-1 font-bold"
      >
        <XCircle size={12} strokeWidth={2.2} /> Invalid
      </span>
    );
  }, [result]);

  return (
    <section
      data-testid="payload-tester-pane"
      className="bg-white border border-[#e7e9ee] rounded-xl p-4 sm:p-5"
    >
      <div className="flex items-center gap-2 mb-3">
        <FlaskConical size={14} strokeWidth={2} className="text-[#0072bc]" />
        <h3 className="font-display font-semibold text-[15px] text-[#0e1420]">Payload Tester</h3>
        <span className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">
          · Platform admin · Validates against <span className="font-mono-data">/api/ingest/dry-run</span> — nothing persisted
        </span>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {/* LEFT — input */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <label
              htmlFor="payload-tester-endpoint"
              className="text-[11px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold shrink-0"
            >
              Endpoint
            </label>
            <select
              id="payload-tester-endpoint"
              data-testid="payload-tester-endpoint"
              value={endpoint}
              onChange={(e) => loadSample(e.target.value)}
              className="flex-1 border border-[#e7e9ee] rounded-lg px-2.5 py-1.5 text-[13px] font-mono-data outline-none focus:border-[#0072bc] bg-white"
            >
              {ENDPOINTS.map((ep) => (
                <option key={ep} value={ep}>{ep}</option>
              ))}
            </select>
          </div>
          <textarea
            data-testid="payload-tester-textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            spellCheck={false}
            className="w-full h-[280px] border border-[#e7e9ee] rounded-lg px-3 py-2 text-[12.5px] font-mono-data leading-[1.55] outline-none focus:border-[#0072bc] resize-y bg-[#fafbfc] text-[#0e1420]"
            placeholder='{"key": "value"}'
          />
          <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => loadSample(endpoint)}
              data-testid="payload-tester-reset-sample"
              className="text-[12px] font-semibold text-[#1c6a99] hover:underline"
            >
              Reset to sample
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={running}
              data-testid="payload-tester-submit"
              className="cc-btn-primary text-[13px] py-2 px-3 inline-flex items-center gap-1.5 disabled:opacity-60"
            >
              <Play size={13} strokeWidth={2} />
              {running ? "Validating…" : "Validate payload"}
            </button>
          </div>
          {parseErr && (
            <div
              data-testid="payload-tester-parse-err"
              className="mt-2 text-[12px] font-mono-data text-[#c73535] bg-[#fdecec] border border-[#f4c9c9] rounded-md px-2.5 py-1.5"
            >
              {parseErr}
            </div>
          )}
        </div>

        {/* RIGHT — result */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="text-[11px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">
              Result
            </div>
            {statusBadge}
            {result?.model && (
              <span className="text-[11px] font-mono-data text-[color:var(--cc-ink-muted)]">
                {result.model}
              </span>
            )}
            {result && (
              <button
                type="button"
                onClick={copyResult}
                data-testid="payload-tester-copy"
                className="ml-auto text-[11.5px] font-semibold text-[#1c6a99] hover:underline inline-flex items-center gap-1"
              >
                {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy JSON</>}
              </button>
            )}
          </div>

          {!result ? (
            <div className="h-[280px] border border-dashed border-[#e7e9ee] rounded-lg flex items-center justify-center text-[12.5px] text-[color:var(--cc-ink-muted)] text-center px-4">
              Pick an endpoint, tweak the payload, and hit <span className="font-semibold text-[#0e1420]">Validate</span>.
              <br />Nothing is written to the database.
            </div>
          ) : (
            <div className="h-[280px] overflow-y-auto border border-[#e7e9ee] rounded-lg bg-[#fafbfc]" data-testid="payload-tester-result">
              {/* Errors */}
              {result.errors && result.errors.length > 0 && (
                <div className="p-3 border-b border-[#eef1f6]">
                  <div className="flex items-center gap-1.5 mb-2 text-[11px] uppercase tracking-widest text-[#c73535] font-bold">
                    <XCircle size={12} strokeWidth={2.2} /> Errors ({result.errors.length})
                  </div>
                  <ul className="space-y-1.5">
                    {result.errors.map((err, i) => (
                      <li
                        key={i}
                        data-testid={`payload-tester-error-${i}`}
                        className="text-[12px] font-mono-data bg-[#fdecec] border border-[#f4c9c9] rounded px-2 py-1.5 text-[#0e1420]"
                      >
                        <div className="font-semibold text-[#c73535]">
                          {(err.loc || []).join(".") || "(root)"}
                        </div>
                        <div className="text-[11.5px] text-[#4b5563] mt-0.5">{err.msg}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Dropped fields */}
              {result.dropped_fields && result.dropped_fields.length > 0 && (
                <div className="p-3 border-b border-[#eef1f6]">
                  <div className="flex items-center gap-1.5 mb-2 text-[11px] uppercase tracking-widest text-[#8a6a1c] font-bold">
                    <AlertTriangle size={12} strokeWidth={2.2} /> Silently dropped ({result.dropped_fields.length})
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {result.dropped_fields.map((f) => (
                      <span
                        key={f}
                        data-testid={`payload-tester-dropped-${f}`}
                        className="text-[11.5px] font-mono-data bg-[#fff6dc] text-[#8a6a1c] border border-[#f0dfa5] rounded px-2 py-0.5"
                      >
                        {f}
                      </span>
                    ))}
                  </div>
                  <div className="text-[11px] text-[color:var(--cc-ink-muted)] mt-1.5">
                    These fields aren&apos;t in the model and would be discarded at the API boundary.
                  </div>
                </div>
              )}

              {/* Parsed */}
              {result.valid && result.parsed && (
                <div className="p-3">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="text-[11px] uppercase tracking-widest text-[#0e8a68] font-bold">
                      Parsed (what would be persisted)
                    </div>
                    <button
                      type="button"
                      onClick={copyAsCurl}
                      data-testid="payload-tester-copy-curl"
                      title="Copy a curl command with $API_URL + $TOKEN placeholders and this exact JSON body"
                      className="ml-auto inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#1c6a99] hover:underline"
                    >
                      {curlCopied ? (
                        <><Check size={12} strokeWidth={2.2} /> Copied</>
                      ) : (
                        <><Terminal size={12} strokeWidth={2} /> Copy as curl</>
                      )}
                    </button>
                  </div>
                  <pre
                    data-testid="payload-tester-parsed"
                    className="text-[11.5px] font-mono-data leading-[1.55] whitespace-pre-wrap break-words text-[#0e1420]"
                  >
                    {JSON.stringify(result.parsed, null, 2)}
                  </pre>
                </div>
              )}

              {/* Empty-body no-errors case (all-optional valid payload) */}
              {result.valid &&
                (!result.errors || result.errors.length === 0) &&
                (!result.dropped_fields || result.dropped_fields.length === 0) &&
                !result.parsed && (
                  <div className="p-3 text-[12.5px] text-[color:var(--cc-ink-muted)]">
                    Valid — no errors, no dropped fields, no parsed body returned.
                  </div>
                )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
